/**
 * OutcomesReviewDialog — Phase 4.5
 *
 * Single dialog that adapts its form to the goal's measurement_type
 * (PERCENT / MILESTONE / CUSTOM / GAS / COPM). On submit it posts to
 * `POST /api/goals/{id}/outcome`.
 *
 * Performs client-side validation per measurement type and surfaces
 * any backend validation errors. Will not submit invalid payloads.
 */
import React, { useState, useMemo } from 'react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { AlertCircle } from 'lucide-react';

const TYPES = ['PERCENT', 'MILESTONE', 'CUSTOM', 'GAS', 'COPM'];

function validate(mtype, body) {
  if (mtype === 'PERCENT') {
    if (body.score == null || Number.isNaN(body.score)) return 'Score is required.';
    if (body.score < 0 || body.score > 100) return 'Score must be between 0 and 100.';
  } else if (mtype === 'MILESTONE') {
    if (!body.milestones_total || body.milestones_total < 1) return 'Total milestones must be at least 1.';
    if (body.milestones_achieved == null || body.milestones_achieved < 0) return 'Achieved milestones cannot be negative.';
    if (body.milestones_achieved > body.milestones_total) return 'Achieved cannot exceed total.';
  } else if (mtype === 'CUSTOM') {
    if (body.baseline == null || body.target == null || body.current == null) return 'Baseline, target and current are required.';
    if (body.baseline === body.target) return 'Baseline and target must differ.';
  } else if (mtype === 'GAS') {
    if (body.level == null || body.level < -2 || body.level > 2) return 'GAS level must be between -2 and +2.';
  } else if (mtype === 'COPM') {
    if (body.performance == null || body.performance < 1 || body.performance > 10) return 'Performance must be 1–10.';
    if (body.satisfaction == null || body.satisfaction < 1 || body.satisfaction > 10) return 'Satisfaction must be 1–10.';
  }
  return null;
}

export function OutcomesReviewDialog({ open, onOpenChange, goal, onRecorded }) {
  const defaultType = goal?.measurement_type || 'PERCENT';
  const [mtype, setMtype] = useState(defaultType);
  const [score, setScore] = useState(50);
  const [milestonesAchieved, setMilestonesAchieved] = useState(0);
  const [milestonesTotal, setMilestonesTotal] = useState(5);
  const [baseline, setBaseline] = useState(goal?.measurement_config?.baseline_value ?? 0);
  const [target, setTarget] = useState(goal?.measurement_config?.target_value ?? 10);
  const [current, setCurrent] = useState(0);
  const [unit, setUnit] = useState(goal?.measurement_config?.unit || '');
  const [level, setLevel] = useState(0);
  const [performance, setPerformance] = useState(5);
  const [satisfaction, setSatisfaction] = useState(5);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const payload = useMemo(() => {
    const body = { measurement_type: mtype, note };
    if (mtype === 'PERCENT') body.score = Number(score);
    else if (mtype === 'MILESTONE') {
      body.milestones_achieved = Number(milestonesAchieved);
      body.milestones_total = Number(milestonesTotal);
    } else if (mtype === 'CUSTOM') {
      body.baseline = Number(baseline);
      body.target = Number(target);
      body.current = Number(current);
      if (unit) body.unit = unit;
    } else if (mtype === 'GAS') body.level = Number(level);
    else if (mtype === 'COPM') {
      body.performance = Number(performance);
      body.satisfaction = Number(satisfaction);
    }
    return body;
  }, [mtype, score, milestonesAchieved, milestonesTotal, baseline, target,
      current, unit, level, performance, satisfaction, note]);

  const clientError = validate(mtype, payload);

  const submit = async () => {
    if (!goal) return;
    setErrorMsg(null);
    const ce = validate(mtype, payload);
    if (ce) { setErrorMsg(ce); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/goals/${goal.id}/outcome`, payload);
      onRecorded?.(r.data.data);
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      setErrorMsg(typeof detail === 'string' ? detail : 'Failed to record outcome. Please review the values and try again.');
    }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="outcomes-review-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Record Goal Outcome</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {goal && (
            <p className="text-xs text-muted-foreground">
              {goal.goal_no} · {goal.title}
            </p>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Measurement type</Label>
            <Select value={mtype} onValueChange={setMtype}>
              <SelectTrigger className="h-8 text-sm" data-testid="outcomes-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {mtype === 'PERCENT' && (
            <div className="space-y-1">
              <Label className="text-xs">Score (0-100)</Label>
              <Input type="number" min="0" max="100" value={score}
                     onChange={e => setScore(e.target.value)}
                     className="h-8 text-sm" data-testid="outcomes-percent-score" />
            </div>
          )}

          {mtype === 'MILESTONE' && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Achieved</Label>
                <Input type="number" min="0" value={milestonesAchieved}
                       onChange={e => setMilestonesAchieved(e.target.value)}
                       className="h-8 text-sm" data-testid="outcomes-milestones-achieved" /></div>
              <div><Label className="text-xs">Total</Label>
                <Input type="number" min="1" value={milestonesTotal}
                       onChange={e => setMilestonesTotal(e.target.value)}
                       className="h-8 text-sm" data-testid="outcomes-milestones-total" /></div>
            </div>
          )}

          {mtype === 'CUSTOM' && (
            <div className="grid grid-cols-4 gap-3">
              <div><Label className="text-xs">Unit</Label>
                <Input value={unit} onChange={e => setUnit(e.target.value)}
                       className="h-8 text-sm" data-testid="outcomes-custom-unit" /></div>
              <div><Label className="text-xs">Baseline</Label>
                <Input type="number" value={baseline}
                       onChange={e => setBaseline(e.target.value)}
                       className="h-8 text-sm" data-testid="outcomes-custom-baseline" /></div>
              <div><Label className="text-xs">Target</Label>
                <Input type="number" value={target}
                       onChange={e => setTarget(e.target.value)}
                       className="h-8 text-sm" data-testid="outcomes-custom-target" /></div>
              <div><Label className="text-xs">Current</Label>
                <Input type="number" value={current}
                       onChange={e => setCurrent(e.target.value)}
                       className="h-8 text-sm" data-testid="outcomes-custom-current" /></div>
            </div>
          )}

          {mtype === 'GAS' && (
            <div className="space-y-1">
              <Label className="text-xs">GAS Level (-2 much less, 0 expected, +2 much better)</Label>
              <Select value={String(level)} onValueChange={(v) => setLevel(v)}>
                <SelectTrigger className="h-8 text-sm" data-testid="outcomes-gas-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['-2','-1','0','1','2'].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {mtype === 'COPM' && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Performance (1-10)</Label>
                <Input type="number" min="1" max="10" value={performance}
                       onChange={e => setPerformance(e.target.value)}
                       className="h-8 text-sm" data-testid="outcomes-copm-performance" /></div>
              <div><Label className="text-xs">Satisfaction (1-10)</Label>
                <Input type="number" min="1" max="10" value={satisfaction}
                       onChange={e => setSatisfaction(e.target.value)}
                       className="h-8 text-sm" data-testid="outcomes-copm-satisfaction" /></div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Note (optional)</Label>
            <Input value={note} onChange={e => setNote(e.target.value)}
                   placeholder="Context for this outcome record"
                   className="h-8 text-sm" data-testid="outcomes-note" />
          </div>

          {errorMsg && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700"
                 data-testid="outcomes-error">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit}
                  disabled={submitting || !!clientError}
                  data-testid="outcomes-submit"
                  title={clientError || ''}>
            {submitting ? 'Saving…' : 'Record Outcome'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
